<template>
  <div class="organizeGridControls">
    <div class="organizeGridControls-viewWrapper">
      <neb-control grouped>
        <neb-control>
          <neb-icon-button icon="grid"
            tooltip="Card View"
            :selected="view === 'grid'"
            @click="$emit('change-view', 'grid')"
          ></neb-icon-button>
        </neb-control>

        <neb-control>
          <neb-icon-button icon="table"
            tooltip="Table View"
            :selected="view === 'table'"
            @click="$emit('change-view', 'table')"
          ></neb-icon-button>
        </neb-control>
      </neb-control>
    </div>

    <div class="organizeGridControls-sortWrapper withViews">
      <div class="organizeGridControls-sortWrapper-valueWrapper">
        <neb-select
          v-model="sortValue"
          :options="options.sortOptions"
          prefix="Sort By"
          defaultValue="views"
        ></neb-select>
      </div>

      <div class="organizeGridControls-sortWrapper-orderWrapper">
        <neb-control grouped>
          <neb-control v-for="(opt ,idx) in options.sortOrderOptions"
            :key="idx"
          >
            <neb-icon-button :icon=" opt === 'asc' ? 'arrowUp' : 'arrowDown'"
              :tooltip="orderTooltip(opt)"
              :selected="sortOrder === opt"
              @click="sortOrder = opt"
            ></neb-icon-button>
          </neb-control>
        </neb-control>
      </div>
    </div>

    <div class="organizeGridControls-actionsWrapper">
      <div class="organizeGridControls-actionsWrapper-assetsSelected"
        v-show="assetsSelectedCount > 0"
      >
        <span>{{ assetsSelectedCount }} asset(s) selected</span>
      </div>
      <div class="organizeGridControls-actionsWrapper-selectAll">
        <a class="selectAll"
          @click="$emit('select-all')"
        >
          Select All
        </a>

        <div class="divider"></div>

        <a class="unselectAll"
          :class="{ disabled: !areAssetsSelected }"
          :disabled="!areAssetsSelected"
          @click="$emit('unselect-all')"
        >
          Unselect All
        </a>
      </div>

      <neb-action-button text="Actions"
        :dropdownOptions="computedActionOptions"
        @select="onAction"
      ></neb-action-button>
    </div>
  </div>
</template>

<script>
import {
  nonMetricSortOptions,
  sortOrderOptions,
  collectionActions
} from '@/options'

export default {
  name: 'OrganizeGridControls',

  props: {
    sort: {
      type: Object,
      default: () => ({
        value: '',
        order: ''
      })
    },
    view: {
      type: String,
      default: 'grid'
    },
    selectedAssetIndexes: {
      type: Array,
      default: () => []
    },
    selectedCollectionId: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    options: {
      sortOptions: [...nonMetricSortOptions],
      sortOrderOptions: [...sortOrderOptions]
    }
  }),

  computed: {
    assetsSelectedCount () { return this.selectedAssetIndexes.length },
    sortValue: {
      get () { return this.sort.value },
      set (value) { this.$emit('changeSort', { key: 'value', value }) }
    },
    sortOrder: {
      get () { return this.sort.order },
      set (value) { this.$emit('changeSort', { key: 'order', value }) }
    },
    computedActionOptions () {
      // Collection selected. All options available if assets selected.
      if (this.selectedCollectionId !== '') {
        if (this.selectedAssetIndexes.length < 1) {
          return collectionActions
            .map(opt => Object.assign({}, opt, { disabled: true }))
        } else {
          return collectionActions
        }
      }

      // Collection not selected and no assets selected. No options available.
      if (this.selectedAssetIndexes.length < 1) {
        return collectionActions
          .map(opt => Object.assign({}, opt, { disabled: true }))
      }

      // Collection not selected; assets are selected. Everything but remove
      // is available.
      return collectionActions
        .map(opt => {
          if (opt.value === 'removeFromCollection') {
            return Object.assign({}, opt, { disabled: true })
          } else {
            return opt
          }
        })
    },
    areAssetsSelected () {
      return this.selectedAssetIndexes.length > 0
    }
  },

  methods: {
    orderTooltip (ord) {
      const dateValues = ['Oldest', 'Newest']
      const orderValues = ['Lowest', 'Highest']
      const [tooltipDown, tooltipUp] = this.sortValue === 'created_date' ? dateValues : orderValues
      return ord === 'asc' ? `${tooltipDown} to ${tooltipUp}` : `${tooltipUp} to ${tooltipDown}`
    },
    onAction (opt) {
      switch (opt.value) {
        case 'createCollection': return this.$emit('create-collection')
        case 'addToCollection': return this.$emit('add-to-collection')
        case 'removeFromCollection': return this.$emit('remove-from-collection')
        default: return
      }
    }
  }
}
</script>

<style lang="scss">
.organizeGridControls {
  padding-left: 20px;
  padding-bottom: 10px;
  display: flex;
  align-items: center;

  &-sortWrapper {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    &.withViews {
      margin-left: 10px;
    }

    &-valueWrapper {
      margin-right: 1em;
    }
  }

  &-actionsWrapper {
    display: flex;
    margin-left: auto;
    align-items: center;

    &-assetsSelected {
      margin-right: 20px;
    }

    .nebIconButton {
      margin-left: 5px;
    }

    &-selectAll {
      display: flex;
      align-items: center;
      margin-right: 20px;

      .divider {
        width: 2px;
        background-color: #cecece;
        height: 15px;
        margin: 0 0.5em;
      }

      a {
        margin: 0;

        &.disabled {
          color: #cecece;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
